import { useState, KeyboardEvent } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  CircularProgress,
  Alert,
} from '@mui/material';
import PersonAddOutlinedIcon from '@mui/icons-material/PersonAddOutlined';
import useAlertStore from '@/stores/alertStore';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { useCurrentPlanFeatures } from '@/hooks/useCurrentPlanFeatures';
import { useOrganizations, useOrganizationInvites } from '../hooks/useOrganizations';
import type { Organization } from '../types';

interface AddMemberModalProps {
  open: boolean;
  organization: Organization | null;
  onClose: () => void;
}

const MESSAGE_MAX = 500;

const AddMemberModal = ({ open, organization, onClose }: AddMemberModalProps) => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const showAlert = useAlertStore((state) => state.showAlert);
  const { inviteMember } = useOrganizations();
  const { defaultSeats } = useCurrentPlanFeatures();
  const { data: invitesData } = useOrganizationInvites(organization?.id, open);

  const pendingInviteCount = (invitesData?.invites ?? []).filter((i) => i.status === 'pending').length;
  const usedSeats = (organization?.member_emails.length ?? 0) + pendingInviteCount;
  const atSeatLimit = defaultSeats !== null && usedSeats >= defaultSeats;

  const handleClose = () => {
    setEmail('');
    setMessage('');
    setError(null);
    onClose();
  };

  const handleInvite = async () => {
    if (!email.trim() || !organization) {
      return;
    }
    if (atSeatLimit) {
      setError(`Seat limit reached (${defaultSeats}). Remove a member or revoke a pending invite first.`);
      return;
    }

    const normalizedEmail = email.trim().toLowerCase();

    // Client-side check: already a member?
    const normalizedMemberEmails = organization.member_emails.map((e) => e.toLowerCase());
    if (normalizedMemberEmails.includes(normalizedEmail)) {
      setError('This person is already a member of the team.');
      return;
    }

    setIsSending(true);
    setError(null);

    try {
      await inviteMember({
        organizationId: organization.id,
        email: normalizedEmail,
        message: message.trim() || undefined,
      });
      showAlert(`Invite sent to ${normalizedEmail}.`, 'success');
      handleClose();
    } catch (err: unknown) {
      // noteGoApiFetcher throws the parsed JSON body (e.g. {detail: "..."}) without
      // attaching response.status — match on the detail string instead of status code.
      const errorObj = err as { detail?: unknown };
      const detail = typeof errorObj.detail === 'string' ? errorObj.detail : '';
      const detailLower = detail.toLowerCase();

      if (detailLower.includes('seat limit')) {
        setError(detail);
      } else if (detailLower.includes('pending')) {
        setError('An invite to this email is already pending.');
      } else if (detailLower.includes('already a member')) {
        setError('This person is already a member of the team.');
      } else if (detailLower.includes('only the') && detailLower.includes('owner')) {
        setError('Only the team owner can invite members.');
      } else {
        // Fall back to the backend's own explanation when it gave one — a
        // hardcoded string here hid every unrecognised reason the invite failed.
        setError(getApiErrorMessage(err, 'Failed to send invite. Please try again.'));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleInvite();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <PersonAddOutlinedIcon />
        Invite Member to {organization?.name}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Send an email invite. They&apos;ll get a link to join your team — works even if they don&apos;t have a NoteGo
          account yet.
        </Typography>

        {defaultSeats !== null && (
          <Alert severity={atSeatLimit ? 'warning' : 'info'} sx={{ mb: 2 }}>
            {usedSeats} of {defaultSeats} seats used
            {atSeatLimit && ' — remove a member or revoke a pending invite to add another.'}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          autoFocus
          label="Email address"
          type="email"
          placeholder="colleague@company.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          onKeyDown={handleKeyDown}
          fullWidth
          disabled={isSending}
          error={!!error}
          sx={{ mb: 2 }}
        />

        <TextField
          label="Personal note (optional)"
          placeholder="Hey! Joining our team so we can share notes — looking forward to working with you."
          value={message}
          onChange={(e) => setMessage(e.target.value.slice(0, MESSAGE_MAX))}
          fullWidth
          multiline
          minRows={3}
          maxRows={6}
          disabled={isSending}
          helperText={`${message.length}/${MESSAGE_MAX} — included in the invite email`}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isSending}>
          Cancel
        </Button>
        <Button
          onClick={handleInvite}
          variant="contained"
          disabled={!email.trim() || isSending || atSeatLimit}
          startIcon={isSending ? <CircularProgress size={16} /> : <PersonAddOutlinedIcon />}
        >
          {isSending ? 'Sending...' : 'Send Invite'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddMemberModal;
